import React, { useState, useEffect, useContext } from 'react';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import Loading from 'tbk-components/src/components/Loading';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';

/**
 * @usePostData
 * @post_type logo
 * @forceLinkedData
 */
export interface ILogoProps {
	title?: string;
	link?: string;
	/**
	 * @control IImage
	 */
	logo?: {
		src: string;
		srcSet?: string;
		alt?: string;
	};
}

export interface ILogoBarProps extends IBaseElementProps {
	layout?: 'Simple' | 'Partners';
	/**
	 * Tagline only displays in Simple layout
	 */
	tagline?: string;
	/**
	 * Heading only displays in Partners layout
	 */
	heading?: string;
	/**
	 * Excerpt only displays in Partners layout
	 * @control richtext
	 */
	excerpt?: string;
	logos?: ILogoProps[];
	backgroundColor?: 'White' | 'Light';
}

/**
 * Calian Logo Bar
 * @icon schedule
 * @block
 */
const LogoBar: React.FC<ILogoBarProps> = withAppContext(
	({
		id,
		className,
		classNames = [],
		tagline,
		layout = 'Simple',
		heading,
		excerpt,
		logos,
		backgroundColor = 'White',
		context,
	}) => {
		const { Api } = useContext(context);
		const [logoData, setLogoData] = useState<ILogoProps[]>([]);
		const [loading, setLoading] = useState<boolean>(false);

		const ConditionalWrapper = ({ condition, wrapper, children }) =>
			condition != '' ? wrapper(children) : children;

		// Update when props change in the editor.
		useEffect(() => {
			if (!logos || logos.length === 0) {
				return;
			}
			// The src attribute won't be set on logos pulled from posts
			// in the editor, so we need to fetch the image data from the API.
			// This is only called in th editor, on the front-end, we can
			// use tbk_gutenberg_props_for_hydration to populate the
			// image props server-side.
			if (!logos[0]?.logo?.src) {
				setLoading(true);
				Api.getLogoBarData({ logos: logos })
					.then(({ logos }) => {
						setLogoData(logos);
					})
					.finally(() => {
						setLoading(false);
					});
			} else {
				// On the front-end, props_for_hydration will populate the logos.
				setLogoData(logos);
			}
		}, [JSON.stringify(logos)]);

		if (loading) {
			return <Loading />;
		}

		let content = null;
		switch (layout) {
			case 'Simple':
				content = (
					<div
						className={cn(
							`calian-logo-bar container-fluid mw-1440px ${
								tagline ? 'd-flex' : 'd-block'
							} flex-column flex-xl-row flex-wrap align-items-center justify-content-center py-3`,
							{
								[`bg-${kebabCase(backgroundColor)}`]:
									backgroundColor,
							},
						)}
					>
						{tagline && (
							<div className="calian-logo-bar__tagline me-lg-3 text-center text-lg-start mb-3 mb-lg-0">
								<p className="fw-light mb-0">{tagline}</p>
							</div>
						)}
						{logoData && logoData.length > 0 && (
							<div className="calian-logo-bar__listing d-flex flex-wrap justify-content-center gap-3 my-lg-3">
								{logoData.map(
									(logoInfo, index) => (
										<>
										{ logoInfo && 
											<span
												className={cn(
													'calian-logo-bar__logo mx-4 mx-md-5 mx-xl-6 mx-xxl-6 my-2 p-0 d-flex align-items-center',
												)}
											>
												<ConditionalWrapper
													condition={logoInfo.link}
													wrapper={(children) => (
														<a
															key={index}
															target="_blank"
															href={logoInfo.link}
														>
															{children}
														</a>
													)}
												>
													{logoInfo.logo && logoInfo.logo.src && (
														<img
															src={logoInfo.logo.src}
															srcSet={
																logoInfo.logo.srcSet || ''
															}
															alt={logoInfo.logo.alt || logoInfo.title}
															className="img-fluid"
														/>
													)}
												</ConditionalWrapper>
											</span>
										}
										</>
									),
								)}
							</div>
						)}
					</div>
				);
				break;
			case 'Partners':
				content = (
					<div
						className={cn('calian-logo-bar container-fluid', {
							[`bg-${kebabCase(backgroundColor)}`]:
								backgroundColor,
						})}
					>
						<div className="row mw-1440px mx-auto pt-6 pb-6 pt-lg-12 pb-lg-8">
							{(heading || excerpt) && (
								<div className="mw-753px col-12 px-0 mx-auto text-center">
									{heading && (
										<h2 className="calian-logo-bar__heading mb-0 text-center">
											{heading}
										</h2>
									)}
									{excerpt && (
										<div
											className="calian-logo-bar__excerpt body-lg mt-3 mb-0 text-center"
											dangerouslySetInnerHTML={{
												__html: excerpt,
											}}
										></div>
									)}
								</div>
							)}

							<div className="col-12">
								{logoData && logoData.length > 0 && (
									<div className="calian-logo-bar__listing container-fluid mt-2 pt-4 pt-sm-8 pb-1">
										<div className="d-flex gap-4 gap-sm-6 mw-11080px w-100 flex-wrap mx-auto justify-content-center">
											{logoData.map(
												(logoInfo, index) => (
													<>
													{logoInfo &&
														<div
															key={index}
															className="d-flex justify-content-center align-items-center"
														>
															<span
																className={
																	'calian-logo-bar__logo p-0 d-flex align-items-center'
																}
															>
																<ConditionalWrapper
																	condition={logoInfo.link}
																	wrapper={(
																		children,
																	) => (
																		<a
																			href={
																				logoInfo.link
																			}
																			target="_blank"
																		>
																			{
																				children
																			}
																		</a>
																	)}
																>
																	<img
																		src={
																			logoInfo.logo.src
																		}
																		srcSet={
																			logoInfo.logo.srcSet
																		}
																		alt={
																			logoInfo.logo.alt ||
																			logoInfo.title
																		}
																		className="img-fluid"
																	/>
																</ConditionalWrapper>
															</span>
														</div>
													}
													</>
												),
											)}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				);
				break;
		}
		return (
			<div
				id={id}
				className={
					className ||
					cn('calian-logo-bar__outer-container', ...classNames)
				}
			>
				{content}
			</div>
		);
	},
);

export default LogoBar;
